import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as THREE from 'three';
import productsData from '../data/products';
import { buildWorld } from './scene/world';
import { createControls } from './scene/controls';
import { createProducts, findNearest } from './scene/products';
import { createFrames } from './scene/frames';
import { addDecor } from './scene/decor';
import { createBarista } from './scene/barista';
import MasterChat from './MasterChat';

const isTouchDevice = () =>
  typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

// ── スマホ用バーチャルスティック ──
const VirtualStick = ({ moveRef }) => {
  const baseRef = useRef(null);
  const [knob, setKnob] = useState({ x: 0, y: 0, active: false });
  const touchId = useRef(null);

  const RADIUS = 50;

  const handleStart = (e) => {
    const t = e.changedTouches[0];
    touchId.current = t.identifier;
    setKnob((k) => ({ ...k, active: true }));
  };
  const handleMove = (e) => {
    if (touchId.current === null || !baseRef.current) return;
    for (const t of e.changedTouches) {
      if (t.identifier !== touchId.current) continue;
      const rect = baseRef.current.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      let dx = t.clientX - cx;
      let dy = t.clientY - cy;
      const len = Math.hypot(dx, dy);
      if (len > RADIUS) {
        dx = (dx / len) * RADIUS;
        dy = (dy / len) * RADIUS;
      }
      moveRef.current.move.x = dx / RADIUS;
      moveRef.current.move.y = dy / RADIUS;
      setKnob({ x: dx, y: dy, active: true });
    }
  };
  const handleEnd = (e) => {
    for (const t of e.changedTouches) {
      if (t.identifier === touchId.current) {
        touchId.current = null;
        moveRef.current.move.x = 0;
        moveRef.current.move.y = 0;
        setKnob({ x: 0, y: 0, active: false });
      }
    }
  };

  return (
    <div
      className={`mv-stick${knob.active ? ' mv-stick--active' : ''}`}
      ref={baseRef}
      onTouchStart={handleStart}
      onTouchMove={handleMove}
      onTouchEnd={handleEnd}
      onTouchCancel={handleEnd}
    >
      <div
        className="mv-stick__knob"
        style={{ transform: `translate(${knob.x}px, ${knob.y}px)` }}
      />
    </div>
  );
};

// ── ストーリーパネルポップアップ ──
const PanelPopup = ({ panel, onClose }) => (
  <div className="mv-popup-backdrop" onClick={onClose}>
    <div className="mv-popup" onClick={(e) => e.stopPropagation()}>
      <button type="button" className="mv-popup__close" onClick={onClose} aria-label="閉じる">×</button>
      <img className="mv-popup__img" src={panel.img} alt={panel.title} />
      <div className="mv-popup__en">{panel.en}</div>
      <h2 className="mv-popup__name">{panel.title}</h2>
      <p className="mv-popup__desc">{panel.text}</p>
    </div>
  </div>
);

// ── 商品ポップアップ ──
const ProductPopup = ({ product, onClose }) => (
  <div className="mv-popup-backdrop" onClick={onClose}>
    <div className="mv-popup" onClick={(e) => e.stopPropagation()}>
      <button type="button" className="mv-popup__close" onClick={onClose} aria-label="閉じる">×</button>
      <img className="mv-popup__img" src={product.img} alt={product.nameJa} />
      <div className="mv-popup__en">{product.name}</div>
      <h2 className="mv-popup__name">{product.nameJa}</h2>
      <div className="mv-popup__meta">
        {product.farm ? `${product.farm}　` : ''}{product.weight}
      </div>
      <div className="mv-popup__price">{product.price}</div>
      <p className="mv-popup__desc">{product.desc}</p>
      <a className="mv-popup__buy" href={product.url} target="_blank" rel="noopener noreferrer">
        BASEショップで購入する
      </a>
    </div>
  </div>
);

const MetaverseShop = () => {
  const canvasRef = useRef(null);
  const controlsRef = useRef(null);
  const inputRef = useRef({ move: { x: 0, y: 0 } });
  const nearestRef = useRef(null);
  const popupOpenRef = useRef(false);
  const [locked, setLocked] = useState(false);
  const [webglFailed, setWebglFailed] = useState(false);
  const [nearest, setNearest] = useState(null);
  const [popup, setPopup] = useState(null);
  const [touch] = useState(isTouchDevice);
  const [entered, setEntered] = useState(false);
  const [assetsReady, setAssetsReady] = useState(false);
  const [progress, setProgress] = useState(0);

  const openNearest = useCallback(() => {
    const target = nearestRef.current;
    if (!target || popupOpenRef.current) return;
    popupOpenRef.current = true;
    setPopup(target);
    if (document.pointerLockElement) document.exitPointerLock();
  }, []);

  const closePopup = useCallback(() => {
    popupOpenRef.current = false;
    setPopup(null);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
    } catch (err) {
      setWebglFailed(true);
      return undefined;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.12;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.05, 60);

    const manager = new THREE.LoadingManager();
    manager.onProgress = (url, loaded, total) => {
      setProgress(total > 0 ? Math.round((loaded / total) * 100) : 100);
    };
    manager.onLoad = () => setAssetsReady(true);
    manager.onError = () => setAssetsReady(true); // 画像1枚の失敗で入店不能にしない

    const textureLoader = new THREE.TextureLoader(manager);
    const { colliders, productAnchors, pendants } = buildWorld(scene);
    const decor = addDecor(scene, colliders);
    const barista = createBarista(scene);
    const interactables = [
      barista.interactable,
      ...createProducts(scene, productsData, productAnchors),
      ...createFrames(scene, textureLoader),
    ];
    const controls = createControls(camera, canvas, colliders, inputRef.current, setLocked);
    controlsRef.current = controls;

    // 近接判定（対象が変わった時だけReact stateを更新）
    let lastNearestId = null;
    const updateNearest = () => {
      const found = popupOpenRef.current ? null : findNearest(interactables, controls.getPosition());
      const id = found ? found.id : null;
      if (id !== lastNearestId) {
        lastNearestId = id;
        interactables.forEach((it) => it.setHighlight(it === found));
        nearestRef.current = found;
        setNearest(found ? { id: found.id, label: found.label, type: found.type } : null);
      }
    };

    // PointerLock中のクリック / Eキーで近くの商品を開く
    const handleCanvasClick = () => {
      if (document.pointerLockElement === canvas) openNearest();
    };
    const handleKey = (e) => {
      if (e.code === 'KeyE') openNearest();
    };
    canvas.addEventListener('click', handleCanvasClick);
    document.addEventListener('keydown', handleKey);

    const clock = new THREE.Clock();
    let rafId;
    const animate = () => {
      rafId = requestAnimationFrame(animate);
      const dt = Math.min(clock.getDelta(), 0.05);
      const elapsed = clock.elapsedTime;
      controls.update(dt);
      updateNearest();
      decor.update(elapsed);
      barista.update(elapsed);
      // ペンダントライトの炎のようなゆらぎ
      pendants.forEach((p, i) => {
        p.intensity = 6 + Math.sin(elapsed * 6.3 + i * 2.1) * 0.25 + Math.sin(elapsed * 11.7 + i) * 0.15;
      });
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
      canvas.removeEventListener('click', handleCanvasClick);
      document.removeEventListener('keydown', handleKey);
      controls.dispose();
      renderer.dispose();
      scene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          mats.forEach((m) => {
            Object.values(m).forEach((v) => {
              if (v && v.isTexture) v.dispose();
            });
            m.dispose();
          });
        }
      });
    };
  }, [openNearest]);

  if (webglFailed) {
    return (
      <div className="mv-fallback">
        <p>お使いの環境では3D店舗を表示できませんでした。</p>
        <Link to="/classic" className="mv-fallback__link">通常版サイトを見る</Link>
      </div>
    );
  }

  const handleEnter = () => {
    setEntered(true);
    if (!touch && controlsRef.current) controlsRef.current.requestLock();
  };

  return (
    <div className="mv-root">
      <canvas ref={canvasRef} className="mv-canvas" />

      <div
        className={`mv-entrance${entered ? ' mv-entrance--hidden' : ''}`}
        style={{ backgroundImage: 'url(/images/generated/hero_bg.jpg)' }}
      >
        <div className="mv-entrance__inner">
          <div className="mv-entrance__en">Specialty Coffee from Komatsu, Ishikawa</div>
          <h1 className="mv-entrance__catch">那谷の森へ、<br />心は向かう。</h1>
          <p className="mv-entrance__tagline">人生の余白時間を、コーヒーとともに。</p>
          <button
            type="button"
            className="mv-entrance__btn"
            onClick={handleEnter}
            disabled={!assetsReady}
          >
            {assetsReady ? '入店する' : '準備中…'}
          </button>
          {!assetsReady && <div className="mv-entrance__progress">LOADING {progress}%</div>}
          <div>
            <Link className="mv-entrance__classic" to="/classic">通常版サイトはこちら</Link>
          </div>
        </div>
      </div>

      <div className="mv-hud">
        <span className="mv-hud__logo">GURI COFFEE</span>
        <nav className="mv-hud__nav">
          <Link to="/about">About</Link>
          <Link to="/blog">Blog</Link>
          <Link to="/classic">通常版</Link>
        </nav>
      </div>

      {entered && !touch && !locked && !popup && (
        <button
          type="button"
          className="mv-lock-hint"
          onClick={() => controlsRef.current && controlsRef.current.requestLock()}
        >
          <span className="mv-lock-hint__title">クリックで店内を歩く</span>
          <span className="mv-lock-hint__sub">WASD / 矢印キー: 移動　マウス: 見回す　Esc: カーソルを戻す</span>
        </button>
      )}

      {entered && locked && !popup && <div className="mv-crosshair" />}

      {entered && nearest && !popup && (
        <button type="button" className="mv-prompt" onClick={openNearest}>
          {nearest.type === 'master' ? 'マスターと話す' : `「${nearest.label}」を見る`}
          {touch ? '（タップ）' : '（クリック / E）'}
        </button>
      )}

      {popup && popup.type === 'product' && (
        <ProductPopup product={popup.data} onClose={closePopup} />
      )}
      {popup && popup.type === 'panel' && (
        <PanelPopup panel={popup.data} onClose={closePopup} />
      )}
      {popup && popup.type === 'master' && <MasterChat onClose={closePopup} />}

      {entered && touch && <VirtualStick moveRef={inputRef} />}
    </div>
  );
};

export default MetaverseShop;
